import {
  borderRadius,
  componentTokens,
  shadows,
  transitions
} from '../KestrixDesignSystem.jsx'
import { colorTokens } from '../tokens/colors.ts'

const viewTabComponent = componentTokens.viewTab
const warningSoft = colorTokens.semantic.status.warningSoftEmphasis
const errorSoft = colorTokens.semantic.status.errorSoftEmphasis
const secondaryHover = colorTokens.buttonStates.secondary.hover

/**
 * View Tab interaction tokens (KDS node 1046:2589).
 *
 * These are raw palette values only. Variants are wired to a palette by
 * `VARIANT_COLORS` in `view-tab-styles.ts`, which is the exhaustive
 * `ViewTabVariant` -> palette map.
 */
export const viewTabTokens = {
  colors: {
    notSelected: {
      text: colorTokens.text.subtextNeutral,
      background: 'transparent',
      border: colorTokens.surface.border.subtle,
      hoverBackground: colorTokens.surface.interactive.hover,
      pressBackground: colorTokens.surface.interactive.pressed,
      menuDot: colorTokens.text.subtextNeutral
    },
    active: {
      background: colorTokens.global.primary[50],
      border: colorTokens.global.primary[50],
      text: colorTokens.semantic.brand.primary,
      menuDot: colorTokens.semantic.brand.primary
    },
    modified: {
      background: warningSoft.surface,
      border: warningSoft.border,
      text: colorTokens.semantic.brand.primary,
      menuDot: colorTokens.semantic.brand.primary
    },
    /** Asterisk on a tab whose name or description draft differs from the saved value. */
    unsavedMark: warningSoft.text,
    newView: {
      background: 'transparent',
      border: 'transparent',
      text: colorTokens.semantic.brand.primary,
      pressBackground: colorTokens.global.primary[50]
    },
    menuButton: {
      notSelectedHover: colorTokens.surface.border.subtle,
      notSelectedPress: colorTokens.surface.interactive.pressed,
      activeHover: colorTokens.global.primary[100],
      activePress: colorTokens.global.primary[200]
    },
    menu: {
      border: colorTokens.surface.border.subtle,
      itemText: colorTokens.text.bodyNeutral,
      itemHover: secondaryHover.surface,
      itemPress: colorTokens.global.primary[100],
      deleteText: colorTokens.semantic.status.error,
      deleteHover: errorSoft.surface,
      deletePress: errorSoft.border,
      icon: colorTokens.text.subtextNeutral,
      panelBackground: colorTokens.semantic.brand.base.white
    },
    focusRing: colorTokens.semantic.brand.primary
  },
  transitions: {
    state: `${transitions.slow}, opacity ${transitions.slow}`,
    pointer: `background ${transitions.fast}`,
    menuReveal: `opacity ${transitions.base}, width ${transitions.base}`,
    menuItem: `background-color ${transitions.fast}`
  },
  /** Milliseconds matching `transitions.base` / `transitions.fast`. */
  popoverTransitionMs: {
    grow: 200,
    fade: 150
  },
  shadows: {
    menu: shadows.dropdown
  },
  sizing: {
    icon: viewTabComponent.icon,
    menuButton: viewTabComponent.menu.button,
    tabTopRadius: viewTabComponent.radius.tabTop,
    activeTabTopRadius: viewTabComponent.radius.activeTabTop,
    menuButtonRadius: viewTabComponent.menu.buttonRadius,
    padding: viewTabComponent.padding,
    minHeight: viewTabComponent.height,
    gap: viewTabComponent.gap,
    fontSize: viewTabComponent.fontSize,
    menuFontSize: viewTabComponent.menu.fontSize,
    menuPanelRadius: borderRadius.md,
    menuOffset: viewTabComponent.menu.offset,
    menuMinWidth: viewTabComponent.menu.minWidth,
    menuButtonInset: viewTabComponent.menu.buttonInset,
    menuItemIcon: viewTabComponent.menu.item.icon,
    menuItemGap: viewTabComponent.menu.item.gap,
    menuItemPaddingX: viewTabComponent.menu.item.paddingX,
    menuItemPaddingY: viewTabComponent.menu.item.paddingY,
    menuDividerMarginX: viewTabComponent.menu.dividerMarginX,
    menuPanelPaddingY: viewTabComponent.menu.panelPaddingY,
    menuReservePadding: `calc(${viewTabComponent.menu.paddingX} + ${viewTabComponent.menu.button}px)`
  }
} as const
